import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BuildingView } from '@aztec-realms/shared-types';
import type { PlacementPreview } from './game/settlement-renderer';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, PrimaryActionButton } from '@aztec-realms/ui';

import {
  availableBuildPlots,
  createSettlementBuilding,
  loadBuildCatalog,
  unlockSettlementDistrict,
  type BuildRequest,
} from './data/settlement-build-api';
import { commandId, verticalSliceQueryKey } from './data/use-vertical-slice';
import { personalProgressKey } from './data/personal-progress-api';
import { HomeBuildingArtwork } from './HomeBuildingArtwork';
import { SavePersonalGoalButton } from './PersonalProgress';
import './settlement-build-picker.css';

export interface BuildPlacementReview {
  building: BuildingView;
  placement: PlacementPreview;
  move: (x: number, y: number) => void;
  busy: boolean;
}

export function SettlementBuildPicker({
  mode = 'build',
  offline,
  locked,
  onCreated,
  onClose,
  playAction,
  onPlay,
  goalOwner,
  initialCode,
  onReviewChange,
  onCamera,
  onManageExisting,
}: {
  onManageExisting?: (buildingId: string) => void;
  onReviewChange?: (review: BuildPlacementReview | undefined) => void;
  onCamera?: (action: 'zoom-in' | 'zoom-out' | 'fit') => void;
  goalOwner?: string;
  initialCode?: string;
  mode?: 'build' | 'districts';
  offline: boolean;
  locked: boolean;
  onCreated: (buildingId: string) => void;
  onClose: () => void;
  playAction?: { label: string; description: string };
  onPlay?: () => void;
}) {
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: ['player', 'building-catalog', 'v1'],
    queryFn: ({ signal }) => loadBuildCatalog(signal),
    enabled: !offline,
    retry: false,
    // This response includes mutable completion/plot/balance state, not only static blueprints.
    // A freshly completed building must unlock its district when the planner is reopened.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const [code, setCode] = useState(initialCode ?? '');
  const [districtCode, setDistrictCode] = useState('');
  const [rotation, setRotation] = useState(0);
  const [plotKey, setPlotKey] = useState('');
  const [confirmation, setConfirmation] = useState<BuildRequest>();
  const reviewRef = useRef<HTMLElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const attempt = useRef<{ payload: string; key: string } | undefined>(undefined);
  const unlockKeys = useRef(new Map<string, string>());
  const unlock = useMutation({
    retry: false,
    mutationFn: async (district: string) => {
      if (offline || locked) throw new Error('Stop safely and reconnect before expanding.');
      const key = unlockKeys.current.get(district) ?? commandId('settlement.expand');
      unlockKeys.current.set(district, key);
      return unlockSettlementDistrict(district, key);
    },
    onSuccess: async (district) => {
      await Promise.all([
        catalog.refetch(),
        queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey }),
      ]);
      setDistrictCode(district.code);
      setPlotKey('');
    },
  });
  const mutation = useMutation({
    retry: false,
    mutationFn: async (body: BuildRequest) => {
      if (offline || locked)
        throw new Error('Construction is unavailable while offline or moving.');
      const payload = JSON.stringify(body);
      if (attempt.current?.payload !== payload)
        attempt.current = { payload, key: commandId('settlement.create') };
      return createSettlementBuilding(body, attempt.current.key);
    },
    onSuccess: async (job) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['player', 'building-catalog'] }),
        queryClient.invalidateQueries({ queryKey: personalProgressKey }),
      ]);
      onCreated(job.building_id);
    },
  });
  const data = catalog.data;
  const options = data
    ? [...data.options].sort(
        (a, b) =>
          Number(data.settlement.buildings.some((item) => item.code === a.code)) -
          Number(data.settlement.buildings.some((item) => item.code === b.code)),
      )
    : [];
  const selected =
    options.find((item) => item.code === (confirmation?.building_code ?? code)) ?? options[0];
  const existingBuilding = data?.settlement.buildings.find((item) => item.code === selected?.code);
  const districts = data?.settlement.districts ?? [];
  const nextDistrict = [...districts]
    .sort((a, b) => a.unlock_order - b.unlock_order)
    .find((item) => !item.unlocked_at);
  const completedLevels =
    data?.settlement.buildings.reduce(
      (sum, item) =>
        sum +
        (data.settlement.construction.find(
          (job) => job.building_id === item.id && job.status === 'active',
        )?.from_level ?? item.level),
      0,
    ) ?? 0;
  const district =
    districts.find((item) => item.code === districtCode) ??
    districts.find((item) => item.unlocked_at);
  const plots =
    selected && data && district
      ? availableBuildPlots(selected, data.settlement, district.code, rotation)
      : [];
  const plot = plots.find((item) => `${item.x},${item.y}` === plotKey) ?? plots[0];
  const costs = Object.entries(selected?.cost ?? {});
  const missing = costs.filter(
    ([resource, amount]) =>
      (data?.balances.find((item) => item.resource_code === resource)?.balance ?? 0) < amount,
  );
  const blocked =
    offline ||
    locked ||
    mutation.isPending ||
    unlock.isPending ||
    !selected ||
    !plot ||
    missing.length > 0;
  // A lost response may follow a successful spend. Retry only the exact submitted
  // confirmation/key; a refreshed balance or occupied plot must not prevent reconciliation.
  const retryingConfirmation =
    mutation.isError &&
    confirmation !== undefined &&
    JSON.stringify(mutation.variables) === JSON.stringify(confirmation);
  const confirmationBlocked =
    offline ||
    locked ||
    mutation.isPending ||
    unlock.isPending ||
    (!retryingConfirmation && blocked);
  const reviewPlots =
    confirmation && selected && data
      ? availableBuildPlots(
          selected,
          data.settlement,
          confirmation.district_code,
          confirmation.rotation,
        )
      : [];
  const validReview =
    !!confirmation &&
    reviewPlots.some((item) => item.x === confirmation.x && item.y === confirmation.y);
  const moveReview = useCallback(
    (x: number, y: number) => {
      if (offline || locked || mutation.isPending || mutation.isError) return;
      setConfirmation((current) => (current ? { ...current, x, y } : current));
    },
    [offline, locked, mutation.isPending, mutation.isError],
  );
  const review = useMemo<BuildPlacementReview | undefined>(() => {
    const selected = data?.options.find((item) => item.code === confirmation?.building_code);
    const reviewDistrict = data?.settlement.districts.find(
      (item) => item.code === confirmation?.district_code,
    );
    if (!confirmation || !selected || !reviewDistrict) return undefined;
    const valid =
      !!data &&
      availableBuildPlots(
        selected,
        data.settlement,
        confirmation.district_code,
        confirmation.rotation,
      ).some((item) => item.x === confirmation.x && item.y === confirmation.y);
    const width = confirmation.rotation % 2 ? selected.height : selected.width;
    const height = confirmation.rotation % 2 ? selected.width : selected.height;
    const id = 'local-build-placement-preview';
    return {
      building: {
        id,
        type: selected.code === 'garden_plaza' ? 'garden' : (selected.code as BuildingView['type']),
        label: selected.label,
        level: 1,
        gridX: confirmation.x,
        gridY: confirmation.y,
        footprintWidth: width,
        footprintHeight: height,
        orientation: confirmation.rotation,
        variant: 'default',
        districtCode: confirmation.district_code,
        visualTier: 0,
        availableBranches: [],
        state: 'idle',
        upgradeCost: [],
        benefit: 'Unbuilt placement preview',
      },
      placement: {
        unobscuredViewport: true,
        buildingId: id,
        x: confirmation.x,
        y: confirmation.y,
        orientation: confirmation.rotation,
        footprintWidth: width,
        footprintHeight: height,
        valid,
        districtBounds: {
          minX: reviewDistrict.min_x,
          maxX: reviewDistrict.max_x + 1,
          minY: reviewDistrict.min_y,
          maxY: reviewDistrict.max_y + 1,
        },
      },
      move: moveReview,
      busy: mutation.isPending || mutation.isError,
    };
  }, [confirmation, data, moveReview, mutation.isPending, mutation.isError]);
  useEffect(() => {
    onReviewChange?.(review);
  }, [onReviewChange, review]);
  useEffect(() => () => onReviewChange?.(undefined), [onReviewChange]);
  const reviewing = !!confirmation;
  useEffect(() => {
    if (reviewing) reviewRef.current?.focus();
  }, [reviewing]);
  const reset = () => {
    setConfirmation(undefined);
    mutation.reset();
    queueMicrotask(() => reviewButtonRef.current?.focus());
  };
  if (confirmation && selected) {
    const adjustmentBlocked = offline || locked || mutation.isPending || mutation.isError;
    return (
      <section
        className="settlement-build-review"
        aria-label="Confirm new structure"
        tabIndex={-1}
        ref={reviewRef}
        data-testid="build-placement-review"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !mutation.isPending) {
            event.preventDefault();
            reset();
          }
        }}
      >
        <header>
          <strong>
            {selected.label} · level 1{' '}
            <small>
              Placement preview · {review?.building.footprintWidth ?? selected.width} ×{' '}
              {review?.building.footprintHeight ?? selected.height} · {confirmation.rotation * 90}°
            </small>
          </strong>
          <button type="button" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </button>
        </header>
        <div
          className="settlement-build-review__tools"
          role="group"
          aria-label="Placement controls"
        >
          <label>
            <span className="ar-visually-hidden">Available plot</span>
            <select
              aria-label="Available plot"
              disabled={adjustmentBlocked}
              value={`${confirmation.x},${confirmation.y}`}
              onChange={(event) => {
                const [x, y] = event.target.value.split(',').map(Number);
                moveReview(x!, y!);
              }}
            >
              {!validReview && (
                <option value={`${confirmation.x},${confirmation.y}`}>
                  Plot {confirmation.x}, {confirmation.y} · blocked
                </option>
              )}
              {reviewPlots.map((item) => (
                <option key={`${item.x},${item.y}`} value={`${item.x},${item.y}`}>
                  Plot {item.x}, {item.y}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            aria-label="Rotate new building clockwise"
            disabled={adjustmentBlocked}
            onClick={() =>
              setConfirmation({ ...confirmation, rotation: (confirmation.rotation + 1) % 4 })
            }
          >
            ↷
          </button>
          {onCamera &&
            (['zoom-out', 'zoom-in', 'fit'] as const).map((action) => (
              <button
                key={action}
                type="button"
                disabled={locked}
                onClick={() => onCamera(action)}
                aria-label={
                  action === 'fit'
                    ? 'Show whole district'
                    : action === 'zoom-in'
                      ? 'Zoom settlement in'
                      : 'Zoom settlement out'
                }
              >
                {action === 'fit' ? 'Fit' : action === 'zoom-in' ? '+' : '−'}
              </button>
            ))}
        </div>
        <p role="status">
          {locked
            ? 'Stop safely before building.'
            : offline
              ? 'Reconnect to confirm construction.'
              : !validReview
                ? 'No free plot fits here. Choose a clear plot or rotate.'
                : 'Drag the preview to place it; drag the ground to look around.'}
        </p>
        <p className="settlement-build-review__cost">
          Spend {costs.map(([resource, amount]) => `${amount} ${resource}`).join(' · ')} on
          confirmation.
        </p>
        {mutation.error && (
          <p role="alert">{mutation.error.message} Retry the same confirmation safely.</p>
        )}
        <div className="settlement-build-review__actions">
          <button type="button" disabled={mutation.isPending} onClick={reset}>
            Change selection
          </button>
          <PrimaryActionButton
            disabled={confirmationBlocked || (!retryingConfirmation && !validReview)}
            loading={mutation.isPending}
            onClick={() => mutation.mutate(confirmation)}
          >
            Confirm construction
          </PrimaryActionButton>
        </div>
      </section>
    );
  }
  return createPortal(
    <div className="settlement-build-overlay">
      <Modal
        open
        title={mode === 'districts' ? 'Expand your settlement' : 'Build a new structure'}
        onClose={() => {
          if (!mutation.isPending && !unlock.isPending) onClose();
        }}
      >
        <div className="settlement-build-picker-shell">
          <div className="settlement-build-picker" data-mode={mode}>
            <p>
              {mode === 'districts'
                ? 'Completed structures open new districts. Review the next expansion and its requirements.'
                : 'Choose what your city gains. Review the result and resources, then choose a plot.'}
            </p>
            {offline && (
              <p role="status">
                Reconnect to load current building choices and start construction.
              </p>
            )}
            {locked && (
              <p role="status">Stop safely before building. Movement protection remains active.</p>
            )}
            {catalog.isPending && !offline && <p role="status">Loading building choices…</p>}
            {catalog.error && (
              <div role="alert">
                {catalog.error.message}
                <button onClick={() => void catalog.refetch()}>Retry catalog</button>
              </div>
            )}
            {selected && data && (
              <>
                {mode === 'districts' && nextDistrict && (
                  <section aria-label="Expand your district">
                    <h3>More room: {nextDistrict.display_name}</h3>
                    <p>
                      Requires {nextDistrict.unlock_requirements.completed_building_levels ?? 0}{' '}
                      completed building levels · your city has {completedLevels}. Earlier districts
                      unlock first.
                    </p>
                    <button
                      disabled={
                        offline ||
                        locked ||
                        mutation.isPending ||
                        unlock.isPending ||
                        Boolean(confirmation) ||
                        completedLevels <
                          (nextDistrict.unlock_requirements.completed_building_levels ?? 0)
                      }
                      onClick={() => unlock.mutate(nextDistrict.code)}
                    >
                      Unlock {nextDistrict.display_name}
                    </button>
                    {unlock.error && <p role="alert">{unlock.error.message}</p>}
                  </section>
                )}
                {mode === 'districts' && !nextDistrict && (
                  <p role="status">
                    All available districts are unlocked. Your city has room to flourish.
                  </p>
                )}
                {mode === 'build' && (
                  <>
                    <fieldset
                      disabled={offline || locked || mutation.isPending || Boolean(confirmation)}
                    >
                      <label>
                        Building family
                        <select
                          value={selected.code}
                          onChange={(event) => {
                            setCode(event.target.value);
                            setPlotKey('');
                            reset();
                          }}
                        >
                          {options.map((item) => (
                            <option key={item.code} value={item.code}>
                              {item.label}
                              {data.settlement.buildings.some(
                                (building) => building.code === item.code,
                              )
                                ? ' · already in your city'
                                : ' · new family'}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="home-building-catalog-preview">
                        <HomeBuildingArtwork code={selected.code} variant="catalog" />
                        <div>
                          <strong>{selected.label}</strong>
                          <p>
                            Your goal: {existingBuilding ? 'another ' : ''}
                            {selected.label} · level 1.
                          </p>
                        </div>
                      </div>
                      <p>
                        {selected.opportunities?.length
                          ? `At completion · World capabilities: ${selected.opportunities.join(', ')}.`
                          : 'At completion · A new structure in your city and one completed level toward district expansion.'}
                      </p>
                      <small>
                        Reviewing a goal spends nothing. Construction starts only after
                        confirmation.
                      </small>
                      {goalOwner && !existingBuilding && (
                        <SavePersonalGoalButton
                          key={selected.code}
                          owner={goalOwner}
                          buildingCode={selected.code}
                          disabled={offline || locked || mutation.isPending}
                        />
                      )}
                      {existingBuilding && onManageExisting && (
                        <button type="button" onClick={() => onManageExisting(existingBuilding.id)}>
                          Manage existing {selected.label}
                        </button>
                      )}
                      <label className="settlement-build-picker__short-field">
                        District
                        <select
                          value={district?.code ?? ''}
                          onChange={(event) => {
                            setDistrictCode(event.target.value);
                            setPlotKey('');
                            reset();
                          }}
                        >
                          {districts.map((item) => (
                            <option disabled={!item.unlocked_at} value={item.code} key={item.code}>
                              {item.display_name}
                              {item.unlocked_at ? '' : ' · locked'}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="settlement-build-picker__short-field">
                        Orientation
                        <select
                          value={rotation}
                          onChange={(event) => {
                            setRotation(Number(event.target.value));
                            setPlotKey('');
                            reset();
                          }}
                        >
                          {[0, 1, 2, 3].map((value) => (
                            <option value={value} key={value}>
                              {value * 90}°
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Available plot
                        <select
                          value={plot ? `${plot.x},${plot.y}` : ''}
                          onChange={(event) => {
                            setPlotKey(event.target.value);
                            reset();
                          }}
                        >
                          {plots.map((item) => (
                            <option key={`${item.x},${item.y}`} value={`${item.x},${item.y}`}>
                              Plot {item.x}, {item.y}
                            </option>
                          ))}
                        </select>
                      </label>
                    </fieldset>
                    {!plot && (
                      <p role="status">
                        No free plot fits this structure here. Choose another district or
                        orientation.
                      </p>
                    )}
                    <section aria-label="Construction cost">
                      <h3>Construction cost</h3>
                      <ul>
                        {costs.map(([resource, amount]) => (
                          <li key={resource}>
                            {amount} {resource} · you have{' '}
                            {data.balances.find((item) => item.resource_code === resource)
                              ?.balance ?? 0}
                            {(data.balances.find((item) => item.resource_code === resource)
                              ?.balance ?? 0) < amount
                              ? ` · need ${amount - (data.balances.find((item) => item.resource_code === resource)?.balance ?? 0)} more`
                              : ' · ready'}
                          </li>
                        ))}
                      </ul>
                    </section>
                    <p>
                      {selected.durationSeconds === 0
                        ? 'Ready as soon as it is placed.'
                        : `Construction time: ${selected.durationSeconds < 60 ? `${selected.durationSeconds} seconds` : selected.durationSeconds < 3600 ? `${Math.ceil(selected.durationSeconds / 60)} minutes` : `${Math.ceil(selected.durationSeconds / 3600)} hours`}. You can play while it builds.`}
                    </p>
                    {missing.length > 0 && (
                      <p role="status">
                        Need more {missing.map(([resource]) => resource).join(', ')} before
                        construction can start.
                      </p>
                    )}
                    {missing.length > 0 && playAction && onPlay && (
                      <section aria-label="Play toward your building goal">
                        <p>{playAction.description}</p>
                        <button disabled={offline || locked || mutation.isPending} onClick={onPlay}>
                          {playAction.label}
                        </button>
                      </section>
                    )}
                  </>
                )}
              </>
            )}
            {mutation.error && (
              <p role="alert">
                {mutation.error.message} Your city has not been replaced. Retry the same
                confirmation safely.
              </p>
            )}
          </div>
          {mode === 'build' && selected && data && (
            <footer className="settlement-build-picker__actions">
              <button
                type="button"
                className="ar-button ar-button--primary"
                ref={reviewButtonRef}
                disabled={blocked}
                onClick={() => {
                  if (selected && district && plot)
                    setConfirmation({
                      building_code: selected.code,
                      district_code: district.code,
                      x: plot.x,
                      y: plot.y,
                      rotation,
                    });
                }}
              >
                Review placement
              </button>
            </footer>
          )}
        </div>
      </Modal>
    </div>,
    document.querySelector('.ar-game-shell') ?? document.body,
  );
}
